import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Stethoscope, Info } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t, formatMoney } from '../i18n';
import { Button, Input, Select, Field, Alert, Spinner } from '../components/ui';

/**
 * A professional applies to join the practice.
 *
 * They enter their own details — including discipline and licence number, which
 * they know and admin does not. Nothing is active until admin approves; until
 * then the record sits pending and out of the directory patients choose from.
 *
 * The subscription fee is shown before they commit and must be accepted
 * explicitly. What they were shown is recorded with the request, because the
 * price list can change and the figure they agreed to is the one that counts.
 */

const ProfessionalSignupView = () => {
  const [disciplines, setDisciplines] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
    phone: '', license: '', disciplineId: '',
  });
  const [feeAccepted, setFeeAccepted] = useState(false);
  const [wantsInvites, setWantsInvites] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const [{ data: disciplineRows }, { data: planRows }] = await Promise.all([
        supabase.from('disciplines').select('id,name').eq('is_active', true).order('name'),
        supabase.from('subscription_plans').select('discipline_id,monthly_amount,annual_amount').eq('is_active', true),
      ]);
      setDisciplines(disciplineRows ?? []);
      setPlans(planRows ?? []);
      setLoadingLists(false);
    };
    load();
  }, []);

  const update = (patch) => {
    setForm((current) => ({ ...current, ...patch }));
    // Changing discipline changes the price, so a previous acceptance no
    // longer refers to what is on screen.
    if (patch.disciplineId !== undefined) setFeeAccepted(false);
  };

  const fee = plans.find((plan) => plan.discipline_id === form.disciplineId);
  const monthly = Number(fee?.monthly_amount ?? 0);
  const annual = fee?.annual_amount != null ? Number(fee.annual_amount) : null;

  const submit = async (event) => {
    event.preventDefault();
    setError(null);

    const required = ['firstName', 'lastName', 'email', 'password', 'disciplineId'];
    if (required.some((field) => !form[field]?.trim?.())) { setError(t('register.fillRequired')); return; }
    if (form.password.length < 8) { setError(t('register.passwordTooShort')); return; }
    if (form.password !== form.confirmPassword) { setError(t('register.passwordMismatch')); return; }
    if (!feeAccepted) { setError(t('professionalSignup.mustAcceptFee')); return; }

    setLoading(true);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: form.email.trim().toLowerCase(),
      password: form.password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          professional_request: true,
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          phone: form.phone.trim(),
          license_number: form.license.trim(),
          primary_discipline_id: form.disciplineId,
          fee_accepted: true,
          fee_monthly: monthly,
          fee_annual: annual,
          wants_calendar_invites: wantsInvites,
        },
      },
    });
    setLoading(false);

    if (signUpError) { setError(signUpError.message); return; }

    if (data.session) {
      await supabase.rpc('claim_professional_request');
      navigate('/', { replace: true });
      return;
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-slate-900">{t('professionalSignup.sentTitle')}</h1>
          <p className="mt-2 text-sm text-slate-600">{t('professionalSignup.sentBody')}</p>
          <Button className="mt-6 w-full" onClick={() => navigate('/ingresar')}>{t('auth.login')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
      <div className="mx-auto my-8 max-w-xl">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-indigo-900">{t('common.appName')}</h1>
          <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-slate-600">
            <Stethoscope size={15} />{t('professionalSignup.title')}
          </p>
        </div>

        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          {loadingLists ? <Spinner /> : (
            <div className="space-y-4">
              {error && <Alert tone="danger">{error}</Alert>}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('register.firstName')} required>
                  <Input value={form.firstName} onChange={(e) => update({ firstName: e.target.value })} />
                </Field>
                <Field label={t('register.lastName')} required>
                  <Input value={form.lastName} onChange={(e) => update({ lastName: e.target.value })} />
                </Field>
              </div>

              <Field label={t('auth.email')} required>
                <Input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('auth.password')} required hint={t('register.passwordHint')}>
                  <Input type="password" value={form.password} onChange={(e) => update({ password: e.target.value })} />
                </Field>
                <Field label={t('register.confirmPassword')} required>
                  <Input type="password" value={form.confirmPassword} onChange={(e) => update({ confirmPassword: e.target.value })} />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('professionals.license')} hint={t('professionalSignup.licenseHint')}>
                  <Input value={form.license} onChange={(e) => update({ license: e.target.value })} />
                </Field>
                <Field label={t('register.phone')}>
                  <Input type="tel" value={form.phone} onChange={(e) => update({ phone: e.target.value })} />
                </Field>
              </div>

              <Field label={t('specializations.discipline')} required hint={t('professionalSignup.disciplineHint')}>
                <Select value={form.disciplineId} onChange={(e) => update({ disciplineId: e.target.value })}>
                  <option value="">{t('professionalSignup.pickDiscipline')}</option>
                  {disciplines.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Select>
              </Field>

              {/* The fee depends on the discipline, so it only appears once one
                  is chosen — and re-appears for acceptance if they change it. */}
              {form.disciplineId && (
                <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-4">
                  <h3 className="flex items-center gap-1.5 font-semibold text-indigo-900">
                    <Info size={15} />{t('professionalSignup.feeTitle')}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-6">
                    <div>
                      <p className="text-xs uppercase text-slate-500">{t('subscriptions.monthlyFee')}</p>
                      <p className="text-2xl font-bold text-indigo-900">{formatMoney(monthly)}</p>
                    </div>
                    {annual != null && (
                      <div>
                        <p className="text-xs uppercase text-slate-500">{t('subscriptions.annualFee')}</p>
                        <p className="text-2xl font-bold text-indigo-900">{formatMoney(annual)}</p>
                        {monthly > 0 && annual < monthly * 12 && (
                          <p className="text-xs text-emerald-700">
                            {t('professionalSignup.annualSaving', {
                              percent: Math.round((1 - annual / (monthly * 12)) * 100),
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {monthly === 0 && (
                    <p className="mt-2 text-xs text-slate-600">{t('professionalSignup.feeNotSet')}</p>
                  )}

                  <label className="mt-4 flex items-start gap-2 text-sm text-slate-800">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={feeAccepted}
                      onChange={(e) => setFeeAccepted(e.target.checked)}
                    />
                    <span>{t('professionalSignup.acceptFee', { amount: formatMoney(monthly) })}</span>
                  </label>
                </div>
              )}

              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={wantsInvites}
                  onChange={(e) => setWantsInvites(e.target.checked)}
                />
                <span>{t('profile.calendarInvites')}</span>
              </label>

              <Alert tone="info">{t('professionalSignup.approvalNotice')}</Alert>

              <Button type="submit" loading={loading} className="w-full">
                {t('professionalSignup.submit')}
              </Button>
            </div>
          )}

          <p className="mt-6 border-t border-slate-100 pt-5 text-center text-sm text-slate-600">
            {t('professionalSignup.alreadyAdded')}{' '}
            <Link to="/activar" className="font-semibold text-indigo-600 hover:underline">
              {t('auth.activateHere')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ProfessionalSignupView;
